import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../lib/supabase';
import { FonteFavoritaInput, Fonte } from '../types/fonte';
import { toast } from 'react-toastify';
import { useAuth } from '../contexts/AuthContext';

export function useFontesFavoritas() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  // Query para listar fontes favoritas do usuário atual
  const { data: fontesFavoritas, isLoading: listLoading } = useQuery({
    queryKey: ['fontes_favoritas', user?.id],
    queryFn: async () => {
      if (!user) return [];

      const { data, error } = await supabase
        .from('fontes_favoritas')
        .select(`
          id,
          id_fonte,
          fontes:id_fonte (
            id,
            nome,
            id_montadora,
            nome_montadora,
            arquivo,
            demonstracao,
            tamanho,
            espacamento
          )
        `)
        .eq('id_usuario', user.id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      
      // Transformar os dados para retornar as fontes com uma flag de favorito
      return data.map((item: any) => ({
        ...item.fontes,
        favorito_id: item.id
      })) || [];
    },
    enabled: !!user,
  });

  // Verificar se uma fonte específica é favorita
  const isFonteFavorita = (id_fonte: string): boolean => {
    if (!fontesFavoritas) return false;
    return fontesFavoritas.some((fonte: Fonte & { favorito_id: string }) => fonte.id === id_fonte);
  };

  // Obter o ID do favorito para uma fonte específica
  const getFavoritoId = (id_fonte: string): string | null => {
    if (!fontesFavoritas) return null;
    const favorito = fontesFavoritas.find((fonte: Fonte & { favorito_id: string }) => fonte.id === id_fonte);
    return favorito ? favorito.favorito_id : null;
  };

  // Mutation para adicionar fonte aos favoritos
  const { mutateAsync: adicionarFavorito } = useMutation({
    mutationFn: async (id_fonte: string) => {
      if (!user) throw new Error('Usuário não autenticado');

      const favorito: FonteFavoritaInput = {
        id_usuario: user.id,
        id_fonte
      };

      const { error } = await supabase
        .from('fontes_favoritas')
        .insert([favorito]);

      if (error) throw error;
      return true;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['fontes_favoritas', user?.id] });
    },
    onError: (error: any) => {
      toast.error('Erro ao adicionar favorito: ' + error.message);
    },
  });

  // Mutation para remover fonte dos favoritos
  const { mutateAsync: removerFavorito } = useMutation({
    mutationFn: async (id_favorito: string) => {
      const { error } = await supabase
        .from('fontes_favoritas')
        .delete()
        .eq('id', id_favorito);

      if (error) throw error;
      return true;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['fontes_favoritas', user?.id] });
    },
    onError: (error: any) => {
      toast.error('Erro ao remover favorito: ' + error.message);
    },
  });

  // Função para alternar o estado de favorito
  const toggleFavorito = async (fonte: Fonte) => {
    const favoritoId = getFavoritoId(fonte.id);
    
    if (favoritoId) {
      await removerFavorito(favoritoId);
    } else {
      await adicionarFavorito(fonte.id);
    }
  };

  return {
    fontesFavoritas,
    listLoading,
    isFonteFavorita,
    adicionarFavorito,
    removerFavorito,
    toggleFavorito,
    getFavoritoId
  };
} 